/**
 * DiagnosticsPage — Phase 6 sub-commit 8 (13 Jun 2026).
 *
 * Hidden support route at `/diagnostics`. Renders a JSON snapshot
 * of the client-side state that's useful when a guest reports a
 * problem (stuck outbox, stale SW, cache issues, install confusion,
 * locale surprises). Provides a "Copy to clipboard" button so the
 * guest can paste the full report into a support email.
 *
 * Why not auth-gated:
 *
 *   The page reads the user's OWN state from their OWN device.
 *   There's no cross-user data exposure risk — userId comes from
 *   useAuth (null when signed out), and every other field is
 *   either browser-global (UA, network) or scoped to the current
 *   user (outbox, install-nudge). A signed-out guest seeing
 *   "userId: null" is the correct diagnostic signal for them, not
 *   a leak.
 *
 *   More importantly: the most common reason a guest would need
 *   to visit /diagnostics is that they're having sign-in or sync
 *   issues, in which case auth-gating the page would defeat its
 *   purpose entirely.
 *
 * Why EN-only inline strings (no i18n namespace):
 *
 *   Scope-bounded. The audience is tech support and Mick himself,
 *   not the steady-state guest. Premium consumer-facing surfaces
 *   are translated; tech-support diagnostic pages are not. If
 *   support volume in non-EN markets later warrants it, i18n is
 *   a clean follow-up — strings live in this one file.
 *
 * Why no "Refresh" or "Clear cache" buttons:
 *
 *   Read-only observation tool by design. Action buttons add UX
 *   safety surface (confirms, undo paths, error states) that's
 *   premature at this stage. Refresh: user can navigate away and
 *   back. Clear cache: a power-user action that should live in
 *   Settings if it ever exists, not in a diagnostic surface.
 */

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ChevronLeft, Copy, Check } from 'lucide-react';
import {
  collectDiagnostics,
  type DiagnosticReport,
} from '../lib/diagnostics';

type CopyState = 'idle' | 'copied' | 'error';

export default function DiagnosticsPage() {
  const { userId } = useAuth();
  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  useEffect(() => {
    let cancelled = false;
    void collectDiagnostics({ userId: userId ?? null }).then(
      (result) => {
        if (!cancelled) setReport(result);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Auto-reset copy-state pill after 2.5s. Tracked separately from
  // the actual write so a fast double-click still gives clean
  // feedback (each click resets the timer via the effect cleanup).
  useEffect(() => {
    if (copyState === 'idle') return;
    const t = setTimeout(() => setCopyState('idle'), 2500);
    return () => clearTimeout(t);
  }, [copyState]);

  const handleCopy = async () => {
    if (!report) return;
    const text = JSON.stringify(report, null, 2);
    // Prefer Clipboard API when available (modern browsers, secure
    // contexts). Falls back to a no-op with an error indicator
    // when unavailable — the report is still visible on-screen,
    // so the guest can manually select + copy as a last resort.
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard?.writeText
    ) {
      try {
        await navigator.clipboard.writeText(text);
        setCopyState('copied');
        return;
      } catch {
        setCopyState('error');
        return;
      }
    }
    setCopyState('error');
  };

  return (
    <>
      <Helmet>
        <title>Diagnostic info · TownInsider</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="min-h-screen bg-cream-50">
        <header
          className="border-b border-moor-100 bg-cream-50/95 backdrop-blur"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="container-narrow flex items-center gap-3 px-4 py-3">
            <Link
              to="/"
              aria-label="Back to home"
              className="flex h-9 w-9 items-center justify-center rounded-full text-moor-600 transition-colors hover:bg-moor-50 hover:text-moor-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs uppercase tracking-wide text-moor-500">
                TownInsider
              </p>
              <h1 className="truncate text-base font-semibold text-moor-900">
                Diagnostic info
              </h1>
            </div>
          </div>
        </header>
        <main className="container-narrow px-4 py-6">
          <p className="text-sm text-moor-700">
            Share this with TownInsider support to help us debug
            problems you're seeing. Tap{' '}
            <strong className="font-semibold">Copy to clipboard</strong>,
            then paste into your reply.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                void handleCopy();
              }}
              disabled={report === null}
              className="inline-flex items-center gap-2 rounded-full bg-moor-700 px-4 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {copyState === 'copied' ? (
                <>
                  <Check className="h-4 w-4" aria-hidden="true" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" aria-hidden="true" />
                  Copy to clipboard
                </>
              )}
            </button>
            {copyState === 'error' && (
              <span
                role="status"
                className="text-xs text-moor-700"
              >
                Couldn't copy automatically — please select the text
                below and copy manually.
              </span>
            )}
          </div>
          <div className="mt-4">
            {report === null ? (
              <p
                aria-live="polite"
                className="text-sm text-moor-500"
              >
                Gathering diagnostic info…
              </p>
            ) : (
              <pre
                className="max-h-[60vh] overflow-auto rounded-2xl bg-white p-4 text-xs leading-relaxed text-moor-900 ring-1 ring-moor-100"
                style={{
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", "Roboto Mono", Consolas, "Courier New", monospace',
                }}
              >
                {JSON.stringify(report, null, 2)}
              </pre>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
